import type {
  AuthResponse,
  CalendarListMode,
  Project,
  Task,
  TaskAssignee,
  TaskWithDetails,
  TaskWorkLog,
  TaskFeedback,
  TaskComment,
  Team,
  TeamRole,
  TeamWithMembers,
  ProjectRole,
  ProjectWithMembers,
  ProjectFeedbackLevel,
  CalendarEvent,
  PublicUser,
  RoleDefinition,
  RolePermissions,
  TeamFeedbackLevel,
  FeedbackLevelRole,
  Label,
  AssignedLabel,
  List,
  ListStatus,
  ListWithItems,
  ListItem,
  ListMember,
  ListTeamShare,
  Organization,
  OrganizationWithRole,
  OrganizationMember,
  TaskStatus,
  TaskStatusOwnerType,
  TaskStatusDefinition,
  ResolvedTaskStatusList,
} from '@projectflow/types';
import type { SetFeedbackLevelsDto, SetTeamFeedbackLevelsDto } from '@projectflow/validation';
import { getSocketId } from '@/lib/socket';

export type {
  RoleDefinition,
  RolePermissions,
  TeamFeedbackLevel,
  Label,
  AssignedLabel,
  Organization,
  OrganizationWithRole,
  OrganizationMember,
};

type LabelType = 'team' | 'project' | 'task' | 'list' | 'general';

function labelListPath(base: string, labelType?: LabelType) {
  return labelType ? `${base}/labels?labelType=${labelType}` : `${base}/labels`;
}

// Shared shape for the identical 8 label routes nested under teams/projects/tasks.
function makeLabelsApi(base: (id: string) => string) {
  return {
    listLabels: (id: string, labelType?: LabelType) =>
      request<Label[]>(labelListPath(base(id), labelType)),

    createLabel: (
      id: string,
      dto: { labelType: LabelType; name: string; color: string; description?: string },
    ) => request<Label>(`${base(id)}/labels`, { method: 'POST', body: JSON.stringify(dto) }),

    updateLabel: (
      id: string,
      labelId: string,
      dto: Partial<{ name: string; color: string; description: string }>,
    ) =>
      request<Label>(`${base(id)}/labels/${labelId}`, {
        method: 'PATCH',
        body: JSON.stringify(dto),
      }),

    deleteLabel: (id: string, labelId: string) =>
      request<void>(`${base(id)}/labels/${labelId}`, { method: 'DELETE' }),

    listAssignableLabels: (id: string, labelType?: LabelType) =>
      request<Label[]>(
        labelType
          ? `${base(id)}/labels/assignable?labelType=${labelType}`
          : `${base(id)}/labels/assignable`,
      ),

    listAssignedLabels: (id: string) => request<AssignedLabel[]>(`${base(id)}/labels/assigned`),

    assignLabel: (id: string, labelId: string, visibleToChildren?: boolean) =>
      request<void>(`${base(id)}/labels/${labelId}/assign`, {
        method: 'POST',
        body: JSON.stringify(visibleToChildren !== undefined ? { visibleToChildren } : {}),
      }),

    unassignLabel: (id: string, labelId: string) =>
      request<void>(`${base(id)}/labels/${labelId}/assign`, { method: 'DELETE' }),
  };
}

function makeVisibleParentLabelsApi(base: (id: string) => string) {
  return {
    listVisibleParentLabels: (id: string) =>
      request<AssignedLabel[]>(`${base(id)}/labels/visible-parents`),
  };
}

// Flat, read-only label lookup by owner — backs global filter UIs (e.g. the
// dashboard's project label filter) that don't have a single nested owner
// route to hang off of.
export const labelsApi = {
  listForOwner: (
    ownerType: 'organization' | 'team' | 'project' | 'list',
    ownerId: string,
    labelType?: LabelType,
  ) => {
    const qs = new URLSearchParams({
      ownerType,
      ownerId,
      ...(labelType ? { labelType } : {}),
    });
    return request<Label[]>(`/labels?${qs}`);
  },
};

// Cache-shaped feedback levels carry `role`/`roleId` as required-but-nullable
// (a faithful read of the DB row), but the PUT request schema declares them
// `.optional()` (omitted, not null). Convert at the wire boundary so callers
// (the editing panels) never have to think about the request shape.
function toFeedbackLevelRoleDto(r: FeedbackLevelRole): { role?: string; roleId?: string } {
  return {
    ...(r.role != null ? { role: r.role } : {}),
    ...(r.roleId != null ? { roleId: r.roleId } : {}),
  };
}

function toFeedbackLevelsDto(levels: ProjectFeedbackLevel[]): SetFeedbackLevelsDto['levels'] {
  return levels.map((l) => ({
    level: l.level,
    name: l.name,
    roles: l.roles.map(toFeedbackLevelRoleDto) as SetFeedbackLevelsDto['levels'][number]['roles'],
  }));
}

function toTeamFeedbackLevelsDto(levels: TeamFeedbackLevel[]): SetTeamFeedbackLevelsDto['levels'] {
  return levels.map((l) => ({
    level: l.level,
    name: l.name,
    roles: l.roles.map(
      toFeedbackLevelRoleDto,
    ) as SetTeamFeedbackLevelsDto['levels'][number]['roles'],
  }));
}

export interface FeedbackQueueItem {
  id: string;
  projectId: string;
  projectName: string;
  title: string;
  feedbackLevel: number | null;
  levelLabel: string | null;
  updatedAt: string;
  // Whether the current user holds one of the roles assigned to this task's
  // current level — false for tasks visible only via broader team oversight.
  canAct: boolean;
}

export interface TeamInvitation {
  id: string;
  email: string | null;
  channel: 'email' | 'telegram';
  role: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  inviterName: string;
}

export interface TeamInviteDetails {
  id: string;
  teamId: string;
  teamName: string;
  email: string | null;
  role: string;
  expiresAt: string;
  acceptedAt: string | null;
  inviterName: string;
}

const API_BASE = import.meta.env['VITE_API_URL'] ?? '/api/v1';

class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly errors?: unknown[],
    // Machine-readable extras the API attaches for the client to act on,
    // e.g. TASKS_NEED_REASSIGNMENT with the statuses to ask about.
    public readonly code?: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

let refreshPromise: Promise<boolean> | null = null;

async function request<T>(path: string, options: RequestInit = {}, isRetry = false): Promise<T> {
  const token = getStoredToken();
  const socketId = getSocketId();
  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(socketId ? { 'X-Socket-Id': socketId } : {}),
      ...options.headers,
    },
  });

  // /auth/refresh and /auth/logout must never trigger another refresh-and-retry
  // cycle on 401 — refresh failing already means the session is dead, and
  // logout failing must not resurrect a refresh attempt (that becomes an
  // infinite refresh<->logout loop once the refresh token is invalid).
  if (res.status === 401 && !isRetry && path !== '/auth/refresh' && path !== '/auth/logout') {
    const { useAuthStore } = await import('@/stores/auth.store');
    refreshPromise ??= useAuthStore
      .getState()
      .refreshSession()
      .finally(() => {
        refreshPromise = null;
      });
    const refreshed = await refreshPromise;
    if (refreshed) return request<T>(path, options, true);
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as {
      message?: string;
      errors?: unknown[];
      code?: string;
      details?: unknown;
    };
    throw new ApiError(
      res.status,
      body.message ?? 'Request failed',
      body.errors,
      body.code,
      body.details,
    );
  }

  if (res.status === 204 || res.headers?.get('content-length') === '0') return undefined as T;
  return res.json() as Promise<T>;
}

function getStoredToken(): string | null {
  return sessionStorage.getItem('access_token');
}

// ── Auth ───────────────────────────────────────────────────────────────────

export const authApi = {
  providers: () => request<{ providers: string[] }>('/auth/providers'),

  telegram: (code: string, codeVerifier: string, redirectUri: string) =>
    request<AuthResponse>('/auth/telegram', {
      method: 'POST',
      body: JSON.stringify({ code, codeVerifier, redirectUri }),
    }),

  requestMagicLink: (email: string) =>
    request<{ message: string }>('/auth/email/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  verifyMagicLink: (token: string) =>
    request<AuthResponse>('/auth/email/verify', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  refresh: (refreshToken: string) =>
    request<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  logout: (refreshToken: string) =>
    request<void>('/auth/logout', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  linkEmailRequest: (email: string) =>
    request<{ message: string }>('/auth/email/link-request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  linkEmailVerify: (token: string) =>
    request<{ email: string; emailVerified: boolean; hasTelegram: boolean }>(
      '/auth/email/link-verify',
      {
        method: 'POST',
        body: JSON.stringify({ token }),
      },
    ),

  linkTelegram: (code: string, codeVerifier: string, redirectUri: string) =>
    request<{ hasTelegram: boolean; avatarUrl: string | null; name: string }>(
      '/auth/telegram/link',
      {
        method: 'POST',
        body: JSON.stringify({ code, codeVerifier, redirectUri }),
      },
    ),
};

// ── Projects ───────────────────────────────────────────────────────────────

export interface LabelFilterParams {
  labelIds?: string[];
  labelMatch?: 'any' | 'all';
  organizationId?: string;
}

function labelFilterQueryString(filters?: LabelFilterParams): string {
  const params: Record<string, string> = {};
  if (filters?.labelIds && filters.labelIds.length > 0) {
    params.labelIds = filters.labelIds.join(',');
  }
  if (filters?.labelMatch) {
    params.labelMatch = filters.labelMatch;
  }
  if (filters?.organizationId) {
    params.organizationId = filters.organizationId;
  }
  const qs = new URLSearchParams(params).toString();
  return qs ? `?${qs}` : '';
}

export const projectsApi = {
  list: (filters?: LabelFilterParams) =>
    request<Project[]>(`/projects${labelFilterQueryString(filters)}`),

  get: (id: string) => request<ProjectWithMembers>(`/projects/${id}`),

  getMyRole: (id: string) =>
    request<{ role: ProjectRole | null; roleId: string | null }>(`/projects/${id}/my-role`),

  create: (data: {
    name: string;
    description?: string;
    organizationId?: string;
    teamId?: string;
    scopeType?: 'predefined' | 'custom';
    startDate: string;
    endDate?: string;
  }) => request<Project>('/projects', { method: 'POST', body: JSON.stringify(data) }),

  update: (
    id: string,
    data: Partial<{
      name: string;
      description: string;
      teamId: string | null;
      startDate: string;
      endDate: string | null;
    }>,
  ) => request<Project>(`/projects/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) => request<void>(`/projects/${id}`, { method: 'DELETE' }),

  updateStatus: (id: string, status: 'active' | 'completed' | 'archived') =>
    request<Project>(`/projects/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  transferOwnership: (id: string, newOwnerId: string) =>
    request<Project>(`/projects/${id}/transfer-ownership`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    }),

  getMembers: (id: string) =>
    request<
      Array<{
        userId: string;
        role: string;
        roleId: string | null;
        roleName: string | null;
        roleDescription: string | null;
        joinedAt: string;
        name: string;
        avatarUrl: string | null;
        email: string;
      }>
    >(`/projects/${id}/members`),

  addMember: (id: string, userId: string, role: string, roleId?: string) => {
    const body = roleId ? { userId, roleId } : { userId, role };
    return request(`/projects/${id}/members`, { method: 'POST', body: JSON.stringify(body) });
  },

  updateMemberRole: (id: string, userId: string, role: string, roleId?: string) => {
    const body = roleId ? { roleId } : { role };
    return request(`/projects/${id}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  removeMember: (id: string, userId: string) =>
    request<void>(`/projects/${id}/members/${userId}`, { method: 'DELETE' }),

  getFeedbackLevels: (id: string) =>
    request<ProjectFeedbackLevel[]>(`/projects/${id}/feedback-levels`),

  setFeedbackLevels: (id: string, levels: ProjectFeedbackLevel[]) =>
    request<ProjectFeedbackLevel[]>(`/projects/${id}/feedback-levels`, {
      method: 'PUT',
      body: JSON.stringify({ levels: toFeedbackLevelsDto(levels) }),
    }),

  getNotificationPreferences: (id: string) =>
    request<ProjectNotificationPreferences>(`/projects/${id}/notification-preferences`),

  updateNotificationPreferences: (
    id: string,
    data: Partial<Omit<ProjectNotificationPreferences, 'projectId' | 'userId' | 'updatedAt'>>,
  ) =>
    request<ProjectNotificationPreferences>(`/projects/${id}/notification-preferences`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  disableNotificationPreferences: (id: string) =>
    request<ProjectNotificationPreferences>(`/projects/${id}/notification-preferences`, {
      method: 'DELETE',
    }),

  listRoles: (projectId: string) => request<RoleDefinition[]>(`/projects/${projectId}/roles`),

  createRole: (
    projectId: string,
    dto: { name: string; permissions: RolePermissions; sortOrder?: number },
  ) =>
    request<RoleDefinition>(`/projects/${projectId}/roles`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  updateRole: (
    projectId: string,
    roleId: string,
    dto: Partial<{ name: string; permissions: RolePermissions; sortOrder: number }>,
  ) =>
    request<RoleDefinition>(`/projects/${projectId}/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  deleteRole: (projectId: string, roleId: string) =>
    request<void>(`/projects/${projectId}/roles/${roleId}`, { method: 'DELETE' }),

  ...makeLabelsApi((id) => `/projects/${id}`),
  ...makeVisibleParentLabelsApi((id) => `/projects/${id}`),
};

// ── Tasks ──────────────────────────────────────────────────────────────────

// ── Task statuses ──────────────────────────────────────────────────────────

const STATUS_OWNER_PATH: Record<TaskStatusOwnerType, string> = {
  organization: 'organizations',
  team: 'teams',
  project: 'projects',
};

export type StatusReassignment = { fromStatusId: string; toStatusId: string };

export type StatusUsage = { statusId: string; taskCount: number };

export type StatusDefinitionInput = {
  name: string;
  color: string;
  baseStatus: Exclude<TaskStatus, 'sent_for_feedback'>;
};

function statusesPath(ownerType: TaskStatusOwnerType, ownerId: string) {
  return `/${STATUS_OWNER_PATH[ownerType]}/${ownerId}`;
}

export const taskStatusesApi = {
  // The owner's resolved list: every available status in column order, with
  // on/off flags, whichever level's saved list is in effect.
  list: (ownerType: TaskStatusOwnerType, ownerId: string) =>
    request<ResolvedTaskStatusList>(`${statusesPath(ownerType, ownerId)}/statuses`),

  create: (ownerType: TaskStatusOwnerType, ownerId: string, dto: StatusDefinitionInput) =>
    request<TaskStatusDefinition>(`${statusesPath(ownerType, ownerId)}/statuses`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  update: (
    ownerType: TaskStatusOwnerType,
    ownerId: string,
    statusId: string,
    dto: Partial<StatusDefinitionInput>,
  ) =>
    request<TaskStatusDefinition>(`${statusesPath(ownerType, ownerId)}/statuses/${statusId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  // Tasks still in the status move to reassignTo; without it, a status in
  // use is refused with TASKS_NEED_REASSIGNMENT.
  remove: (
    ownerType: TaskStatusOwnerType,
    ownerId: string,
    statusId: string,
    reassignTo?: string,
  ) =>
    request<void>(
      `${statusesPath(ownerType, ownerId)}/statuses/${statusId}${
        reassignTo ? `?reassignTo=${encodeURIComponent(reassignTo)}` : ''
      }`,
      { method: 'DELETE' },
    ),

  saveList: (
    ownerType: TaskStatusOwnerType,
    ownerId: string,
    body: { items: { statusId: string; enabled: boolean }[]; reassign?: StatusReassignment[] },
  ) =>
    request<ResolvedTaskStatusList>(`${statusesPath(ownerType, ownerId)}/status-list`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),

  // Task counts per status in the projects that follow this owner's list.
  // Statuses with no tasks are left out.
  usage: (ownerType: TaskStatusOwnerType, ownerId: string) =>
    request<StatusUsage[]>(`${statusesPath(ownerType, ownerId)}/status-usage`),

  resetList: (
    ownerType: TaskStatusOwnerType,
    ownerId: string,
    body: { reassign?: StatusReassignment[] } = {},
  ) =>
    request<ResolvedTaskStatusList>(`${statusesPath(ownerType, ownerId)}/status-list/reset`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
};

// The 409 the status endpoints return when a change would strand tasks;
// its details list what to ask the user about.
export type StrandedStatus = { statusId: string; name: string; taskCount: number };

export function strandedStatusesOf(err: unknown): StrandedStatus[] | null {
  if (!(err instanceof ApiError) || err.code !== 'TASKS_NEED_REASSIGNMENT') return null;
  return (err.details as { statuses?: StrandedStatus[] } | undefined)?.statuses ?? [];
}

export const tasksApi = {
  list: (projectId: string) =>
    request<Array<Task & { assignees: TaskAssignee[] }>>(`/projects/${projectId}/tasks`),

  // Flat, cross-project task list (distinct from `list` above, which is
  // nested under a single project) — backs label-filtered views that need
  // to query across every project the user can see.
  listAll: (filters?: LabelFilterParams) =>
    request<Array<Task & { assignees: TaskAssignee[] }>>(
      `/tasks${labelFilterQueryString(filters)}`,
    ),

  get: (projectId: string, taskId: string) =>
    request<TaskWithDetails>(`/projects/${projectId}/tasks/${taskId}`),

  getFeedback: (projectId: string, taskId: string) =>
    request<TaskFeedback[]>(`/projects/${projectId}/tasks/${taskId}/feedback`),

  getComments: (projectId: string, taskId: string) =>
    request<TaskComment[]>(`/projects/${projectId}/tasks/${taskId}/comments`),

  create: (
    projectId: string,
    data: {
      title: string;
      description?: string;
      parentTaskId?: string;
      startDate: string;
      endDate?: string;
      assigneeIds?: string[];
    },
  ) =>
    request<Task>(`/projects/${projectId}/tasks`, { method: 'POST', body: JSON.stringify(data) }),

  update: (
    projectId: string,
    taskId: string,
    data: Partial<{
      title: string;
      description: string;
      parentTaskId: string | null;
      startDate: string;
      endDate: string | null;
    }>,
  ) =>
    request<Task>(`/projects/${projectId}/tasks/${taskId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  updateStatus: (projectId: string, taskId: string, statusId: string) =>
    request<{ task: Task; previousStatus: TaskStatus; previousStatusId: string }>(
      `/projects/${projectId}/tasks/${taskId}/status`,
      { method: 'PATCH', body: JSON.stringify({ statusId }) },
    ),

  updateAssignees: (projectId: string, taskId: string, assigneeIds: string[]) =>
    request(`/projects/${projectId}/tasks/${taskId}/assignees`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeIds }),
    }),

  addWorkLog: (projectId: string, taskId: string, note: string) =>
    request<TaskWorkLog>(`/projects/${projectId}/tasks/${taskId}/work-logs`, {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),

  addFeedback: (projectId: string, taskId: string, body: string) =>
    request<TaskFeedback>(`/projects/${projectId}/tasks/${taskId}/feedback`, {
      method: 'POST',
      body: JSON.stringify({ body }),
    }),

  addComment: (projectId: string, taskId: string, content: string) =>
    request<TaskComment>(`/projects/${projectId}/tasks/${taskId}/comments`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  escalateFeedback: (projectId: string, taskId: string) =>
    request<Task>(`/projects/${projectId}/tasks/${taskId}/feedback/escalate`, {
      method: 'POST',
    }),

  delete: (projectId: string, taskId: string) =>
    request<void>(`/projects/${projectId}/tasks/${taskId}`, { method: 'DELETE' }),

  getFeedbackQueue: (params?: { organizationId?: string }) => {
    const qs = params?.organizationId
      ? `?${new URLSearchParams({ organizationId: params.organizationId })}`
      : '';
    return request<FeedbackQueueItem[]>(`/tasks/feedback-queue${qs}`);
  },

  // A task never owns a label itself (see LabelsService), so there's no
  // create/update/delete-owned route here — only assignable/assigned/
  // visible-parents/assign/unassign, all nested under the task's project.
  // "Creating a task label" from the task view goes through
  // `projectsApi.createLabel(projectId, { labelType: 'task' | 'general', ... })`.
  listAssignableLabels: (projectId: string, taskId: string, labelType?: LabelType) =>
    request<Label[]>(
      labelType
        ? `/projects/${projectId}/tasks/${taskId}/labels/assignable?labelType=${labelType}`
        : `/projects/${projectId}/tasks/${taskId}/labels/assignable`,
    ),

  listAssignedLabels: (projectId: string, taskId: string) =>
    request<AssignedLabel[]>(`/projects/${projectId}/tasks/${taskId}/labels/assigned`),

  listVisibleParentLabels: (projectId: string, taskId: string) =>
    request<AssignedLabel[]>(`/projects/${projectId}/tasks/${taskId}/labels/visible-parents`),

  assignLabel: (projectId: string, taskId: string, labelId: string, visibleToChildren?: boolean) =>
    request<void>(`/projects/${projectId}/tasks/${taskId}/labels/${labelId}/assign`, {
      method: 'POST',
      body: JSON.stringify(visibleToChildren !== undefined ? { visibleToChildren } : {}),
    }),

  unassignLabel: (projectId: string, taskId: string, labelId: string) =>
    request<void>(`/projects/${projectId}/tasks/${taskId}/labels/${labelId}/assign`, {
      method: 'DELETE',
    }),
};

// ── Lists ──────────────────────────────────────────────────────────────────

export const listsApi = {
  list: (filters?: LabelFilterParams) =>
    request<List[]>(`/lists${labelFilterQueryString(filters)}`),

  get: (listId: string) => request<ListWithItems>(`/lists/${listId}`),

  create: (data: { name: string; date?: string; organizationId?: string }) =>
    request<List>('/lists', { method: 'POST', body: JSON.stringify(data) }),

  update: (
    listId: string,
    data: Partial<{ name: string; date: string | null; autoSortCompleted: boolean }>,
  ) => request<List>(`/lists/${listId}`, { method: 'PATCH', body: JSON.stringify(data) }),

  updateStatus: (listId: string, status: ListStatus) =>
    request<List>(`/lists/${listId}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  delete: (listId: string) => request<void>(`/lists/${listId}`, { method: 'DELETE' }),

  addItem: (
    listId: string,
    data:
      | { kind: 'standalone'; title: string; description?: string; time?: string }
      | { kind: 'task_ref'; taskId: string; title?: string; description?: string; time?: string },
  ) => request<ListItem>(`/lists/${listId}/items`, { method: 'POST', body: JSON.stringify(data) }),

  updateItem: (
    listId: string,
    itemId: string,
    data: Partial<{
      title: string;
      completed: boolean;
      description: string | null;
      time: string | null;
    }>,
  ) =>
    request<ListItem>(`/lists/${listId}/items/${itemId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  removeItem: (listId: string, itemId: string) =>
    request<void>(`/lists/${listId}/items/${itemId}`, { method: 'DELETE' }),

  removeItems: (listId: string, itemIds: string[]) =>
    request<void>(`/lists/${listId}/items/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ itemIds }),
    }),

  bulkAddItems: (
    listId: string,
    items: Array<{ title: string; description?: string; time?: string }>,
  ) =>
    request<ListItem[]>(`/lists/${listId}/items/bulk-add`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    }),

  reorderItems: (listId: string, items: Array<{ itemId: string; position: number }>) =>
    request<ListItem[]>(`/lists/${listId}/items/reorder`, {
      method: 'PATCH',
      body: JSON.stringify({ items }),
    }),

  getMembers: (listId: string) => request<ListMember[]>(`/lists/${listId}/members`),

  addMembers: (listId: string, memberIds: string[], role: 'viewer' | 'contributor') =>
    request<ListMember[]>(`/lists/${listId}/members`, {
      method: 'POST',
      body: JSON.stringify({ memberIds, role }),
    }),

  removeMember: (listId: string, userId: string) =>
    request<void>(`/lists/${listId}/members/${userId}`, { method: 'DELETE' }),

  getTeams: (listId: string) => request<ListTeamShare[]>(`/lists/${listId}/teams`),

  addTeams: (listId: string, teamIds: string[], role: 'viewer' | 'contributor') =>
    request<ListTeamShare[]>(`/lists/${listId}/teams`, {
      method: 'POST',
      body: JSON.stringify({ teamIds, role }),
    }),

  removeTeam: (listId: string, teamId: string) =>
    request<void>(`/lists/${listId}/teams/${teamId}`, { method: 'DELETE' }),

  duplicate: (
    listId: string,
    data: Partial<{ name: string; date: string; onlyIncomplete: boolean }>,
  ) => request<List>(`/lists/${listId}/duplicate`, { method: 'POST', body: JSON.stringify(data) }),

  ...makeLabelsApi((id) => `/lists/${id}`),
};

// ── Teams ──────────────────────────────────────────────────────────────────

export const teamsApi = {
  list: (filters?: LabelFilterParams) =>
    request<TeamWithMembers[]>(`/teams${labelFilterQueryString(filters)}`),

  get: (id: string) => request<TeamWithMembers>(`/teams/${id}`),

  getSubTeams: (id: string) => request<Team[]>(`/teams/${id}/sub-teams`),

  getProjects: (id: string) => request<Project[]>(`/teams/${id}/projects`),

  getMyRole: (id: string) => request<{ role: TeamRole | null }>(`/teams/${id}/my-role`),

  create: (data: {
    name: string;
    description?: string;
    organizationId?: string;
    parentTeamId?: string;
    scopeType?: 'predefined' | 'custom' | 'parent';
    roles?: Array<{ name: string; permissions: RolePermissions; sortOrder?: number }>;
    inheritMembers?: boolean;
  }) => request<TeamWithMembers>('/teams', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<{ name: string; description: string }>) =>
    request<TeamWithMembers>(`/teams/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) => request<void>(`/teams/${id}`, { method: 'DELETE' }),

  transferOwnership: (id: string, newOwnerId: string) =>
    request<Team>(`/teams/${id}/transfer-ownership`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    }),

  addMember: (id: string, userId: string, role: string, roleId?: string) => {
    const body = roleId ? { userId, roleId } : { userId, role };
    return request(`/teams/${id}/members`, { method: 'POST', body: JSON.stringify(body) });
  },

  updateMemberRole: (teamId: string, userId: string, role: string, roleId?: string) => {
    const body = roleId ? { roleId } : { role };
    return request(`/teams/${teamId}/members/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  },

  removeMember: (teamId: string, userId: string) =>
    request<void>(`/teams/${teamId}/members/${userId}`, { method: 'DELETE' }),

  listInvitations: (teamId: string) => request<TeamInvitation[]>(`/teams/${teamId}/invitations`),

  createInvitation: (
    teamId: string,
    data: { email?: string; role: string; roleId?: string; channel?: 'email' | 'telegram' },
  ) =>
    request<TeamInvitation>(`/teams/${teamId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  revokeInvitation: (teamId: string, inviteId: string) =>
    request<void>(`/teams/${teamId}/invitations/${inviteId}`, { method: 'DELETE' }),

  listRoles: (teamId: string) => request<RoleDefinition[]>(`/teams/${teamId}/roles`),

  createRole: (
    teamId: string,
    dto: { name: string; permissions: RolePermissions; sortOrder?: number },
  ) =>
    request<RoleDefinition>(`/teams/${teamId}/roles`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  updateRole: (
    teamId: string,
    roleId: string,
    dto: Partial<{ name: string; permissions: RolePermissions; sortOrder: number }>,
  ) =>
    request<RoleDefinition>(`/teams/${teamId}/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  deleteRole: (teamId: string, roleId: string) =>
    request<void>(`/teams/${teamId}/roles/${roleId}`, { method: 'DELETE' }),

  getTeamFeedbackLevels: (teamId: string) =>
    request<TeamFeedbackLevel[]>(`/teams/${teamId}/feedback-levels`),

  setTeamFeedbackLevels: (teamId: string, levels: TeamFeedbackLevel[]) =>
    request<TeamFeedbackLevel[]>(`/teams/${teamId}/feedback-levels`, {
      method: 'PUT',
      body: JSON.stringify({ levels: toTeamFeedbackLevelsDto(levels) }),
    }),

  ...makeLabelsApi((id) => `/teams/${id}`),
  ...makeVisibleParentLabelsApi((id) => `/teams/${id}`),
};

// ── Organizations ──────────────────────────────────────────────────────────

export interface OrganizationMemberDetails extends OrganizationMember {
  email: string;
}

export interface OrganizationDetails extends Organization {
  members: OrganizationMemberDetails[];
}

export interface OrganizationInvitationRow {
  id: string;
  email: string | null;
  channel: 'email' | 'telegram';
  roleId: string;
  roleName: string;
  token: string;
  expiresAt: string;
  createdAt: string;
  inviterName: string;
}

// Org label routes only expose owned-label CRUD — orgs have no
// assignable/assigned/assign surface (labels are assigned on the entities
// themselves), so only the CRUD subset of the shared label routes applies.
const {
  listLabels: listOrgLabels,
  createLabel: createOrgLabel,
  updateLabel: updateOrgLabel,
  deleteLabel: deleteOrgLabel,
} = makeLabelsApi((id) => `/organizations/${id}`);

export const organizationsApi = {
  list: () => request<OrganizationWithRole[]>('/organizations'),

  get: (id: string) => request<OrganizationDetails>(`/organizations/${id}`),

  create: (data: { name: string; description?: string }) =>
    request<Organization>('/organizations', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<{ name: string; description: string }>) =>
    request<Organization>(`/organizations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  delete: (id: string) => request<void>(`/organizations/${id}`, { method: 'DELETE' }),

  transferOwnership: (id: string, newOwnerId: string) =>
    request<Organization>(`/organizations/${id}/transfer-ownership`, {
      method: 'POST',
      body: JSON.stringify({ newOwnerId }),
    }),

  listMembers: (id: string) => request<OrganizationMemberDetails[]>(`/organizations/${id}/members`),

  addMember: (id: string, userId: string, roleId: string) =>
    request<OrganizationMember>(`/organizations/${id}/members`, {
      method: 'POST',
      body: JSON.stringify({ userId, roleId }),
    }),

  updateMemberRole: (id: string, userId: string, roleId: string) =>
    request<OrganizationMember>(`/organizations/${id}/members/${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ roleId }),
    }),

  removeMember: (id: string, userId: string) =>
    request<void>(`/organizations/${id}/members/${userId}`, { method: 'DELETE' }),

  listInvitations: (id: string) =>
    request<OrganizationInvitationRow[]>(`/organizations/${id}/invitations`),

  createInvitation: (
    id: string,
    data: { email?: string; roleId: string; channel?: 'email' | 'telegram' },
  ) =>
    request<OrganizationInvitationRow>(`/organizations/${id}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  revokeInvitation: (id: string, inviteId: string) =>
    request<void>(`/organizations/${id}/invitations/${inviteId}`, { method: 'DELETE' }),

  listRoles: (id: string) => request<RoleDefinition[]>(`/organizations/${id}/roles`),

  createRole: (
    id: string,
    dto: { name: string; permissions: RolePermissions; sortOrder?: number },
  ) =>
    request<RoleDefinition>(`/organizations/${id}/roles`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  updateRole: (
    id: string,
    roleId: string,
    dto: Partial<{ name: string; permissions: RolePermissions; sortOrder: number }>,
  ) =>
    request<RoleDefinition>(`/organizations/${id}/roles/${roleId}`, {
      method: 'PATCH',
      body: JSON.stringify(dto),
    }),

  deleteRole: (id: string, roleId: string) =>
    request<void>(`/organizations/${id}/roles/${roleId}`, { method: 'DELETE' }),

  /** Every label in the org regardless of owner — feeds the label filters. */
  allLabels: (id: string, labelType?: LabelType) =>
    request<Label[]>(
      labelType
        ? `/organizations/${id}/labels/all?labelType=${labelType}`
        : `/organizations/${id}/labels/all`,
    ),

  listLabels: listOrgLabels,
  createLabel: createOrgLabel,
  updateLabel: updateOrgLabel,
  deleteLabel: deleteOrgLabel,
};

// ── Invitations (public token endpoints) ───────────────────────────────────

export interface OrgInviteDetails {
  id: string;
  organizationId: string;
  organizationName: string;
  email: string | null;
  roleId: string;
  roleName: string;
  expiresAt: string;
  acceptedAt: string | null;
  inviterName: string;
}

export type InviteDetails =
  | ({ type: 'team' } & TeamInviteDetails)
  | ({ type: 'organization' } & OrgInviteDetails);

export type AcceptInviteResult =
  | { type: 'team'; teamId: string; role: string }
  | { type: 'organization'; organizationId: string; roleId: string };

export const invitationsApi = {
  get: (token: string) => request<InviteDetails | null>(`/invitations/${token}`),

  accept: (token: string) =>
    request<AcceptInviteResult>(`/invitations/${token}/accept`, { method: 'POST' }),
};

// ── Users ──────────────────────────────────────────────────────────────────

export interface UserPreferences {
  userId: string;
  theme: string;
  calendarListMode: CalendarListMode;
  notifyEmail: boolean;
  notifyTelegram: boolean;
  notifyFrequency: string;
  digestTime: string;
  notifyOnAssignment: boolean;
  notifyOnTeamAdd: boolean;
  notifyOnProjectMembership: boolean;
  notifyOnStatusChange: boolean;
  notifyOnMention: boolean;
  notifyOnFeedbackReceived: boolean;
  notifyOnFeedbackRequested: boolean;
  notifyOnDueSoon: boolean;
  notifyOnOverdue: boolean;
  updatedAt: string;
}

export interface ProjectNotificationPreferences {
  projectId: string;
  userId: string;
  enabled: boolean;
  notifyEmail: boolean;
  notifyTelegram: boolean;
  notifyFrequency: string;
  digestTime: string;
  notifyOnAssignment: boolean;
  notifyOnProjectMembership: boolean;
  notifyOnStatusChange: boolean;
  notifyOnMention: boolean;
  notifyOnFeedbackReceived: boolean;
  notifyOnFeedbackRequested: boolean;
  notifyOnDueSoon: boolean;
  notifyOnOverdue: boolean;
  updatedAt: string | null;
}

export interface AppNotification {
  id: string;
  userId: string;
  type:
    | 'task_assigned'
    | 'team_added'
    | 'project_added'
    | 'status_changed'
    | 'feedback_received'
    | 'mention_in_comment'
    | 'mention_in_feedback'
    | 'mention_in_work_log'
    | 'mention_in_description'
    | 'task_due_soon'
    | 'task_overdue'
    | 'project_removed'
    | 'feedback_requested';
  title: string;
  body: string;
  link: string | null;
  readAt: string | null;
  createdAt: string;
}

export const usersApi = {
  search: (q: string, organizationId: string) =>
    request<Array<PublicUser & { email: string; hasTelegram: boolean }>>(
      `/users/search?q=${encodeURIComponent(q)}&organizationId=${organizationId}`,
    ),

  updateProfile: (data: { name?: string }) =>
    request<{
      id: string;
      name: string;
      email: string;
      avatarUrl: string | null;
      createdAt: string;
    }>('/users/me', { method: 'PATCH', body: JSON.stringify(data) }),

  getPreferences: () => request<UserPreferences>('/users/me/preferences'),

  updatePreferences: (data: Partial<Omit<UserPreferences, 'userId' | 'updatedAt'>>) =>
    request<UserPreferences>('/users/me/preferences', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  getNotifications: () => request<AppNotification[]>('/users/me/notifications'),

  getUnreadCount: () => request<{ count: number }>('/users/me/notifications/unread-count'),

  markNotificationRead: (id: string) =>
    request<void>(`/users/me/notifications/${id}/read`, { method: 'PATCH' }),

  markAllNotificationsRead: () =>
    request<void>('/users/me/notifications/read-all', { method: 'POST' }),

  dismissNotification: (id: string) =>
    request<void>(`/users/me/notifications/${id}`, { method: 'DELETE' }),
};

// ── Calendar ───────────────────────────────────────────────────────────────

export const calendarApi = {
  getMembers: (params?: { organizationId?: string }) => {
    const qs = params?.organizationId
      ? `?${new URLSearchParams({ organizationId: params.organizationId })}`
      : '';
    return request<Array<{ id: string; name: string; avatarUrl: string | null }>>(
      `/calendar/members${qs}`,
    );
  },

  getEvents: (params: {
    from: string;
    to: string;
    projectId?: string;
    teamId?: string;
    assigneeId?: string;
    status?: string;
    organizationId?: string;
  }) => {
    const qs = new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined)) as Record<
        string,
        string
      >,
    );
    return request<CalendarEvent[]>(`/calendar?${qs}`);
  },
};

// ── Attachments ────────────────────────────────────────────────────────────

export const attachmentsApi = {
  presign: (taskId: string, data: { filename: string; sizeBytes: number; mimeType: string }) =>
    request<{ presignedUrl: string; attachmentId: string }>(
      `/tasks/${taskId}/attachments/presign`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
    ),

  confirm: (attachmentId: string) =>
    request(`/attachments/${attachmentId}/confirm`, { method: 'POST' }),

  getDownloadUrl: (attachmentId: string) =>
    request<{ presignedUrl: string }>(`/attachments/${attachmentId}/download`),
};

export { ApiError };
